import React, { useMemo, useState } from 'react';
import { FiCheckCircle } from 'react-icons/fi';
import { cn } from '@/lib/utils';

function shuffle(array) {
  return [...array].sort(() => Math.random() - 0.5);
}

// Classifica por toque (seleciona item, depois toca na categoria certa — mesmo esquema
// tap-to-select do jogo de pareamento) OU por arrastar-e-soltar no desktop. Os dois convivem
// de graça: touch não dispara os eventos HTML5 de drag, então no mobile só o toque funciona;
// no desktop o aluno pode usar qualquer um dos dois.
// items: [{ id, text, category }]. categories: nomes das colunas.
export function Categorize({ categories, items }) {
  const shuffled = useMemo(() => shuffle(items), [items]);
  const [selected, setSelected] = useState(null);
  const [placed, setPlaced] = useState({});
  const [shakeCategory, setShakeCategory] = useState(null);
  const [draggingId, setDraggingId] = useState(null);
  const [dragOverCategory, setDragOverCategory] = useState(null);

  const pending = shuffled.filter(item => !placed[item.id]);
  const done = pending.length === 0;

  const attemptPlace = (item, category) => {
    if (item.category === category) {
      setPlaced(prev => ({ ...prev, [item.id]: category }));
    } else {
      setShakeCategory(category);
      setTimeout(() => setShakeCategory(null), 500);
    }
  };

  const handleSelectItem = item => {
    if (placed[item.id]) return;
    setSelected(current => (current?.id === item.id ? null : item));
  };

  const handleSelectCategory = category => {
    if (!selected) return;
    attemptPlace(selected, category);
    setSelected(null);
  };

  const handleDrop = (e, category) => {
    e.preventDefault();
    setDragOverCategory(null);
    const id = e.dataTransfer.getData('text/plain');
    const item = shuffled.find(i => String(i.id) === id);
    if (item && !placed[item.id]) attemptPlace(item, category);
  };

  return (
    <div className="flex flex-col gap-5 rounded-2xl border border-solid border-neutral-100 bg-white p-6 shadow-card">
      <p className="font-semibold text-primary-dark">{selected ? `Toque na categoria de "${selected.text}"` : 'Toque ou arraste um item até a categoria certa'}</p>

      {!done && (
        <div className="flex flex-wrap gap-2">
          {pending.map(item => (
            <button
              key={item.id}
              type="button"
              draggable
              onDragStart={e => {
                e.dataTransfer.setData('text/plain', String(item.id));
                setDraggingId(item.id);
              }}
              onDragEnd={() => setDraggingId(null)}
              onClick={() => handleSelectItem(item)}
              className={cn(
                'cursor-grab rounded-full border-2 border-solid px-4 py-2 text-sm font-bold transition-colors active:cursor-grabbing',
                selected?.id === item.id ? 'border-mission bg-mission/10 text-primary-dark' : 'border-neutral-lightest bg-white text-primary-dark',
                draggingId === item.id && 'opacity-40'
              )}
            >
              {item.text}
            </button>
          ))}
        </div>
      )}

      <div
        className="grid gap-3"
        style={{ gridTemplateColumns: `repeat(${categories.length}, minmax(0, 1fr))` }}
      >
        {categories.map(category => {
          const categoryItems = Object.entries(placed)
            .filter(([, cat]) => cat === category)
            .map(([id]) => shuffled.find(i => i.id === id));
          return (
            <button
              key={category}
              type="button"
              onClick={() => handleSelectCategory(category)}
              onDragOver={e => {
                e.preventDefault();
                setDragOverCategory(category);
              }}
              onDragLeave={() => setDragOverCategory(current => (current === category ? null : current))}
              onDrop={e => handleDrop(e, category)}
              className={cn(
                'flex min-h-[140px] flex-col gap-2 rounded-xl border-2 border-dashed p-3 text-left transition-colors',
                selected || dragOverCategory === category ? 'border-mission bg-mission/5' : 'border-neutral-lightest bg-bright',
                shakeCategory === category && 'border-rose-400 bg-rose-50'
              )}
            >
              <span className="text-center text-xs font-bold uppercase tracking-wide text-neutral-500">{category}</span>
              {categoryItems.map(item => (
                <span
                  key={item.id}
                  className="rounded-full bg-success-lightest px-3 py-1.5 text-center text-xs font-bold text-success"
                >
                  {item.text}
                </span>
              ))}
            </button>
          );
        })}
      </div>

      {done && (
        <div className="flex items-center gap-2 rounded-xl bg-success-lightest px-4 py-3 font-bold text-success">
          <FiCheckCircle className="h-5 w-5 shrink-0" />
          Tudo classificado corretamente!
        </div>
      )}
    </div>
  );
}

export default Categorize;
